/**
 * @file 予定の件名 → 分類 (種類 × 領域) の対応表 (`schedule_map_v1.yaml`) の parse。
 *
 * @what: YAML を起動時に検証して in-memory 表現にする。規則は上から先勝ちの部分一致。
 * @why: カレンダーは件名に記法を強要しない自由入力なので、対応の知識はコード側の表に
 *   一元管理する。未知の領域 id や重複 match はここで例外にして、表の壊れをテストで検知する。
 * @failure: 形式違反は ScheduleMapError (メッセージに場所を含める)。
 */

import { attainmentModel } from "./index";
import rawMap from "./schedule_map_v1.yaml";

export interface ScheduleKind {
  id: string;
  label: string;
}

export interface ScheduleRule {
  /** 件名の部分一致文字列。上から順に最初に一致した規則が勝つ。 */
  match: string;
  kind: string;
  /** 効く領域 (attainment model の domain id)。複数なら分を等分する。空 = 種類のみ数える。 */
  domains: string[];
}

export interface ScheduleMap {
  version: number;
  kinds: ScheduleKind[];
  /** 件名の先頭の角括弧タグ [おんがく] の対応。rules より先に引く (省略可)。 */
  tags: ScheduleRule[];
  rules: ScheduleRule[];
}

/** タグに付ける修飾子。この語が入っている回だけ、種類を習い事に上げる。 */
const LESSON_MODIFIER = "習い事";
const LESSON_KIND = "lesson";

export class ScheduleMapError extends Error {}

function fail(message: string): never {
  throw new ScheduleMapError(`schedule map: ${message}`);
}

export function parseScheduleMap(raw: unknown, domainIds: ReadonlySet<string>): ScheduleMap {
  if (typeof raw !== "object" || raw === null) fail("root must be an object");
  const root = raw as Record<string, unknown>;
  if (root.version !== 1) fail(`unsupported version: ${String(root.version)}`);

  if (!Array.isArray(root.kinds) || root.kinds.length === 0) fail("kinds must be non-empty");
  const kinds: ScheduleKind[] = root.kinds.map((k, i) => {
    const kind = k as Record<string, unknown>;
    if (typeof kind.id !== "string" || kind.id === "") fail(`kinds[${i}].id is required`);
    if (typeof kind.label !== "string" || kind.label === "") fail(`kinds[${i}].label is required`);
    return { id: kind.id, label: kind.label };
  });
  const kindIds = new Set(kinds.map((k) => k.id));
  if (kindIds.size !== kinds.length) fail("duplicate kind id");

  const parseRules = (list: unknown, field: string): ScheduleRule[] => {
    if (!Array.isArray(list) || list.length === 0) fail(`${field} must be non-empty`);
    const seenMatch = new Set<string>();
    return list.map((r, i) => {
      const rule = r as Record<string, unknown>;
      if (typeof rule.match !== "string" || rule.match === "") fail(`${field}[${i}].match is required`);
      if (seenMatch.has(rule.match)) fail(`duplicate match in ${field}: ${rule.match}`);
      seenMatch.add(rule.match);
      if (typeof rule.kind !== "string" || !kindIds.has(rule.kind)) {
        fail(`${field}[${i}] (${rule.match}): unknown kind "${String(rule.kind)}"`);
      }
      const domains = rule.domains ?? [];
      if (!Array.isArray(domains)) fail(`${field}[${i}] (${rule.match}): domains must be a list`);
      const seenDomain = new Set<string>();
      for (const d of domains) {
        if (typeof d !== "string" || !domainIds.has(d)) {
          fail(`${field}[${i}] (${rule.match}): unknown domain "${String(d)}"`);
        }
        if (seenDomain.has(d)) fail(`${field}[${i}] (${rule.match}): duplicate domain "${d}"`);
        seenDomain.add(d);
      }
      return { match: rule.match, kind: rule.kind, domains: domains as string[] };
    });
  };

  // tags は任意 (省略・空なら従来どおり件名の部分一致だけで引く)
  const tags = Array.isArray(root.tags) && root.tags.length > 0 ? parseRules(root.tags, "tags") : [];
  const rules = parseRules(root.rules, "rules");

  return { version: 1, kinds, tags, rules };
}

/**
 * 件名から規則を引く。先頭の角括弧タグを最優先で見て、無ければ件名の部分一致に落ちる。
 *
 * タグは `[おんがく]` または `[おんがく: 習い事]` の形。後者はその回だけ種類を習い事にする
 * (家庭練習と教室は件名の語では区別できないため、書き手が明示する)。
 * 未知のタグはタグ無しとして扱い、件名の部分一致に落ちる (黙って捨てない)。
 */
export function ruleForLabel(map: ScheduleMap, label: string): ScheduleRule | null {
  const tagged = /^\s*[[［]([^\]］]*)[\]］]/.exec(label);
  if (tagged) {
    const parts = (tagged[1] ?? "").split(/[:：]/).map((v) => v.trim());
    const head = parts[0] ?? "";
    const tag = map.tags.find((t) => t.match === head);
    if (tag) {
      const isLesson = parts.slice(1).some((v) => v.includes(LESSON_MODIFIER));
      return isLesson ? { ...tag, kind: LESSON_KIND } : tag;
    }
  }
  for (const rule of map.rules) {
    if (label.includes(rule.match)) return rule;
  }
  return null;
}

export const scheduleMap: ScheduleMap = parseScheduleMap(
  rawMap,
  new Set(attainmentModel.domains.map((d) => d.id)),
);
